import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, Subject } from 'rxjs';

import { environment } from "@environments/environment";

import { AuthenticationService } from '@aifs-shared/auth/authentication.service';

import { BaseService } from '@aifs-shared/common/base-service';
import { ResponseData } from '@aifs-shared/common/response-data';
import { BaseResponse, ResponseResultCode } from '@aifs-shared/common/base-response';

import { UploadPolicyResponse } from './upload-policy-response';
import { UploadCompletedResponse } from './upload-completed-response';
import { UploadComplete } from '@aifs-shared/amazon-s3/upload-complete';

import { DocumentType } from './document-type';
import { UploadedFile } from "./uploaded-file";

@Injectable()
export class UploadService extends BaseService {

    constructor(
        private http: HttpClient,
        private authService: AuthenticationService) {
        super();
    }

    /**
     * This does nothing more than fetching the policy and signature from the Upload Service
     * @param documentType 
     * @param payload 
     * @param uploadedById 
     */
    prepareUpload(applicationId: number, options: { documentType: DocumentType, fileExtension: string }): Observable<UploadPolicyResponse> {
        // console.debug('fetching Policy and Signature')

        let params = JSON.stringify({
            imageType: options.documentType,
            fileExtension: options.fileExtension,
            applicationId: applicationId,
            interviewerId: 0 /* for now */
        });

        let subject = new Subject<UploadPolicyResponse>();

        this.http
            .post<BaseResponse>(environment.ServiceUrl_Upload_Prepare, params)
            .subscribe({
                next: (data: any) => {
                    const response = this.getResultData<UploadPolicyResponse>(data);
                    subject.next(response);
                },
                error: (error: any) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    /**
  * This does nothing more than fetching the policy and signature from the Upload Service
  * @param documentType 
  * @param payload 
  * @param uploadedById 
  */
    prepareUploadForInterviewer(interviewerId: number, options: { documentType: DocumentType, fileExtension: string }): Observable<UploadPolicyResponse> {
        // console.debug('fetching Policy and Signature')

        let params = JSON.stringify({
            imageType: options.documentType,
            fileExtension: options.fileExtension,
            applicationId: 0,
            interviewerId: interviewerId
        });

        let subject = new Subject<UploadPolicyResponse>();

        this.http
            .post<BaseResponse>(environment.ServiceUrl_Upload_Prepare, params)
            .subscribe({
                next: (data: any) => {
                    const response = this.getResultData<UploadPolicyResponse>(data);
                    subject.next(response);
                },
                error: (error: any) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    uploadCompletedSuccessfully(uploadComplete: UploadComplete): Observable<UploadCompletedResponse> {
        let subject = new Subject<UploadCompletedResponse>();

        this.http
            .post<BaseResponse>(environment.ServiceUrl_Upload_Completed_Success, uploadComplete)
            .subscribe({
                next: (data: any) => {
                    const response = this.getResultData<UploadCompletedResponse>(data);
                    subject.next(response);
                },
                error: (error: any) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    getUploads(applicationId: number): Observable<UploadsResponse> {
        let subject = new Subject<UploadsResponse>();
        const url = environment.ServiceUrl_Upload_UploadsForApplication(applicationId);
        // console.log('Calling upload API URL:', url);
        this.http
            .get<BaseResponse>(url)
            .subscribe({
                next: (data: BaseResponse) => {
                    const response = this.getResultData<UploadsResponse>(data);
                    subject.next(response);
                },
                error: (error: any) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    getUploadsForType(options: { applicationId: number, documentType: number }): Observable<UploadsForTypeResponse> {
        let subject = new Subject<UploadsForTypeResponse>();
        this.http
            .get<BaseResponse>(environment.ServiceUrl_Upload_UploadsForApplicationAndDocumentType(options))
            .subscribe({
                next: (data: BaseResponse) => {
                    const response = this.getResultData<UploadsForTypeResponse>(data);
                    subject.next(response);
                },
                error: (error: any) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    getUploadLimitsForType(options: { applicationId: number, documentType: number }): Observable<UploadLimitsForTypeResponse> {
        let subject = new Subject<UploadLimitsForTypeResponse>();
        this.http
            .get<BaseResponse>(environment.ServiceUrl_Upload_UploadLimitsForApplicationAndDocumentType(options))
            .subscribe({
                next: (data: BaseResponse) => {
                    const response = this.getResultData<UploadLimitsForTypeResponse>(data);
                    subject.next(response);
                },
                error: (error: any) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    getUploadLimitsForApplication(applicationId: number): Observable<UploadLimitsForApplicationResponse> {
        let subject = new Subject<UploadLimitsForApplicationResponse>();
        this.http
            .get<BaseResponse>(environment.ServiceUrl_Upload_UploadLimitsForApplication(applicationId))
            .subscribe({
                next: (data: BaseResponse) => {
                    const response = this.getResultData<UploadLimitsForApplicationResponse>(data);
                    subject.next(response);
                },
                error: (error: any) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    getUploadById(id: number): Observable<UploadedFile[]> {
        let subject = new Subject<UploadedFile[]>();

        // TODO(ian): Look at the result from this 
        this.http
            .get<BaseResponse>(environment.ServiceUrl_Upload_UploadById(id))
            .subscribe({
                next: (data: BaseResponse) => {
                    const response = this.getResultData<UploadsForTypeResponse>(data);
                    subject.next(response.uploads);
                },
                error: (error: any) => {
                    subject.error(error);
                }
            });

        return subject;

        // return (this.http
        //     .get<BaseResponse>(environment.ServiceUrl_Upload_UploadById(id))
        //     .map(UploadService.extractServiceData)
        //     .catch(this.handleError)
        // ).map((result: UploadsForTypeResponse) => result.uploads)
    }

    getMedicalReportStatus(id: number): Observable<MedicalReportResponse> {
        let subject = new Subject<MedicalReportResponse>();

        this.http
            .get<BaseResponse>(environment.ServiceUrl_Upload_GetMedicalFormStatus(id))
            .subscribe(
                data => {
                    const response = this.getResultData<MedicalReportResponse>(data);
                    subject.next(response);
                },
                error => {
                    subject.error(error);
                });

        return subject;
    }

    deleteUploadById(id: number): Observable<boolean> {
        let subject = new Subject<boolean>();
        this.http
            .post<BaseResponse>(environment.ServiceUrl_Upload_DeleteById(id), null)
            .subscribe(
                data => {
                    const response = data.resultCode == ResponseResultCode.Success;
                    subject.next(response);
                },
                error => {
                    subject.error(error);
                });

        return subject;
    }

    deleteInterviewerProfile(id: number): Observable<boolean> {
        let subject = new Subject<boolean>();
        this.http
            .post<BaseResponse>(environment.ServiceUrl_Upload_DeleteInterviewerProfile(id), null)
            .subscribe({
                next: (data: BaseResponse) => {
                    const response = data.resultCode == ResponseResultCode.Success;
                    subject.next(response);
                },
                error: (error: any) => {
                    subject.error(error);
                }
            });

        return subject;
    }
}

export class UploadsResponse implements ResponseData {
    uploads: UploadedFile[]
    constructor() {
        this.uploads = [];
    }
}


export class UploadsForTypeResponse implements ResponseData {
    documentType: number
    uploads: UploadedFile[]
    canUploadMore: boolean
    constructor() {
        this.documentType = 0;
        this.uploads = [];
        this.canUploadMore = false;
    }
}

export class UploadLimitsForTypeResponse implements ResponseData {
    itemLimit: number
    pageLimit: number
    canUploadMore: boolean
    constructor() {
        this.itemLimit = 0;
        this.pageLimit = 1;
        this.canUploadMore = false;
    }
}

export class UploadLimitsForApplicationResponse implements ResponseData {
    limits: UploadLimit[] = [];
}

export class UploadLimit {
    imageType: number;
    description: string;
    pageLimit: number;
    maximum: number;
    current: number;
    constructor(type: number, desc: string, pages: number, max: number, current: number) {
        this.imageType = type;
        this.description = desc;
        this.pageLimit = pages;
        this.maximum = max;
        this.current = current;
    }
}

export class MedicalReportResponse implements ResponseData {
    applicationId: number;
    medicalReportRequired: boolean;
    medicalReportId: number;
    constructor() {
        this.applicationId = 0;
        this.medicalReportId = 0;
        this.medicalReportRequired = false;
    }
}
